import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import Utils, { ScreenFormat } from '../../utils';
import UserAgentUtils from '../../utils/userAgent';
import BackofficeModeManager from '../../utils/iframeApi/BackofficeModeManager';
import { StoreState } from '../../reducers';
import { AppInfosState, AppType } from '../App/reducer';
import { GalleryInfosState } from './reducer';
import { emptyDownloadCache } from '../DownloadManager/actions';
import { OfflineManagerState } from '../OfflineManager/reducer';
import LeftSideMenu from '../MenuManager/Left';
import SharePanel from '../Share/Panel';
import { initializeGallery } from './actions';
import { GalleryContainer, GalleryChildrenContainer } from './styles';
import { AuthenticationState } from '../Authentication/reducer';
import Splashscreen from '../../components/Splashscreen';
import { DriveListState } from '../DriveItemList/reducer';
import { RoutePath, RouteParams } from '../../routes/constants';
import { RouterParamsState } from '../SetRouteParams/reducer';
import { MenuState } from '../MenuManager/reducer';
import { setMenuConfigMode } from '../MenuManager/actions';
import AuthUtils from '../../utils/authentication';
import { SamlIframeOrigin } from '../Authentication/styles';
import Authentication from '../Authentication';
import DownloadAllPopup from '../DownloadAllPopup';
export const svgSpritePath = require('../../images/icons.svg');

export interface GalleryProps {
	children: React.ReactNode;
	appInfos: AppInfosState;
	galleryInfos: GalleryInfosState;
	offlineManager: OfflineManagerState;
	authentication: AuthenticationState;
	driveList: DriveListState;
	dispatch: Dispatch<StoreState>;
	routerParamsHistory: RouterParamsState[];
	menuManager: MenuState;
}

export interface GalleryState {
	screenFormat: ScreenFormat;
	needConnectionFromCache: boolean;
}

class Gallery extends React.Component<GalleryProps, GalleryState> {
	private backofficeModeManager: BackofficeModeManager;

	constructor(props: GalleryProps) {
		super(props);
		this.onWindowResize = this.onWindowResize.bind(this);
		this.onConnectionIsUp = this.onConnectionIsUp.bind(this);
		this.state = {
			screenFormat: Utils.getScreenFormat(),
			needConnectionFromCache: false
		};

		UserAgentUtils.subscribeToConnectionIsUp(() => {
			this.onConnectionIsUp();
		});
	}

	componentDidMount() {
		const { dispatch, routerParamsHistory, galleryInfos } = this.props;
		const { params, routes } = routerParamsHistory[routerParamsHistory.length - 1];
		const { template } = galleryInfos;

		window.addEventListener('resize', this.onWindowResize, true);

		if (UserAgentUtils.isAndroidApp()) {
			dispatch(emptyDownloadCache());
		}

		dispatch(initializeGallery());

		if (!UserAgentUtils.isMobileApp) {
			const currentPath = routes[routes.length - 1].path as RoutePath;

			this.setPageTitle(currentPath, params, this.props);
		}

		if (template) {
			dispatch(setMenuConfigMode(this.state.screenFormat));
		}

		window.parent.postMessage({
			target: 'GallApp',
			action: 'redirection',
			params: {
				src: window.location.href
			}
			// tslint:disable-next-line:align
		}, '*');
	}

	componentWillReceiveProps(props: GalleryProps) {
		const { galleryInfos, dispatch, routerParamsHistory, authentication } = props;
		const { needConnectionFromCache } = this.state;
		const { isLocalTemplateLoaded, template } = galleryInfos;
		const { mode } = authentication.userInfos;
		const prevMode = this.props.authentication.userInfos.mode;
		const prevTemplate = this.props.galleryInfos.template;

		if (!UserAgentUtils.isMobileApp) {
			const backOfficeModeKey = 'backofficeMode';
			const isBackofficeModeEnabled = window.location.href.includes(backOfficeModeKey);
			const isBackofficeModeManagerCreated = this.backofficeModeManager !== undefined;

			const prevRouterParamsHistory = this.props.routerParamsHistory;
			const prevRoutes = prevRouterParamsHistory[prevRouterParamsHistory.length - 1] && prevRouterParamsHistory[prevRouterParamsHistory.length - 1].routes;
			const { params, routes } = routerParamsHistory[routerParamsHistory.length - 1];

			if (isBackofficeModeEnabled && isLocalTemplateLoaded && !isBackofficeModeManagerCreated ) {
				this.backofficeModeManager = new BackofficeModeManager(dispatch);
			}

			const currentPath = routes[routes.length - 1].path as RoutePath;

			if (prevRoutes !== routes) {
				document.title = '';
			}
			if (!document.title) {
				this.setPageTitle(currentPath, params, props);
			}
		}

		if (prevTemplate !== template) {
			dispatch(setMenuConfigMode(this.state.screenFormat));
		}

		if (mode !== prevMode) {
			if (AuthUtils.isConnected(mode) && (!AuthUtils.isOnCache(mode) && needConnectionFromCache)) {
				this.setState({ needConnectionFromCache: false });
			}
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.onWindowResize);
	}

	onConnectionIsUp = () => {
		const { authentication } = this.props;
		const { mode} = authentication.userInfos;

		if (AuthUtils.needReconnecting(mode) && AuthUtils.isOnCache(mode)) {
			this.setState({needConnectionFromCache: true});
		}
	}

	render() {
		const { children, galleryInfos, offlineManager, authentication, menuManager, appInfos } = this.props;
		const { needConnectionFromCache } = this.state;
		const { forceViewUpdateCount, forceSplashScreen } = galleryInfos;
		const { needCredentials, mode } = authentication.userInfos;
		const { menuModes } = menuManager;
		const appType = appInfos.type as AppType;
		const isWaitingAuthentication = needCredentials && AuthUtils.isNotConnected(mode);
		const isGalleryReady = Utils.isGalleryReady(galleryInfos, offlineManager, appType);

		const shouldDisplaySplashScreen = !(isGalleryReady || isWaitingAuthentication) || forceSplashScreen;

		return (
			<GalleryContainer>
				{isGalleryReady && menuModes && <LeftSideMenu />}
				<SharePanel />
				{isGalleryReady || isWaitingAuthentication ?
					<GalleryChildrenContainer key={forceViewUpdateCount}>
						{children}
					</GalleryChildrenContainer>
				:
					null
				}
				<DownloadAllPopup />
				{needConnectionFromCache && <Authentication origin={SamlIframeOrigin.App} />}
				<Splashscreen display={shouldDisplaySplashScreen} />
			</GalleryContainer>
		);
	}

	setPageTitle = (currentPath: RoutePath, params: RouteParams, props: GalleryProps) => {
		const { galleryInfos, driveList, dispatch, authentication, offlineManager } = props;
		const { config } = galleryInfos;
		const { mode } = authentication.userInfos;
		const isReady = Utils.isGalleryReady(galleryInfos, offlineManager, AppType.Single);
		const isWaitingAuthentication = authentication.userInfos.needCredentials && AuthUtils.isNotConnected(mode);

		if (isReady && !isWaitingAuthentication) {
			document.title = Utils.getPageTitle(currentPath, params, config, driveList, dispatch);
		}
	}

	onWindowResize(): void {
		const { galleryInfos, menuManager, dispatch } = this.props;
		const screenFormat = Utils.getScreenFormat();

		if (galleryInfos.template && !menuManager.menuModes || screenFormat !== this.state.screenFormat) {
			dispatch(setMenuConfigMode(screenFormat));
			this.setState({ screenFormat });
		}
	}
}

const mapStateToProps = (state: StoreState) => {
	const { appInfos, galleryInfos, offlineManager, authentication, driveList, routerParamsHistory, menuManager } = state;
	return { appInfos, galleryInfos, offlineManager, authentication, driveList, routerParamsHistory, menuManager };
};

export default connect(mapStateToProps)(Gallery);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Gallery/index.tsx